
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminListingFilters } from "@/lib/types/adminTypes";
import { CalendarIcon, SearchIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";

interface AdminFilterPanelProps {
  filters: AdminListingFilters;
  onFilterChange: (filters: Partial<AdminListingFilters>) => void;
  onReset: () => void;
}

export function AdminFilterPanel({ filters, onFilterChange, onReset }: AdminFilterPanelProps) {
  const [searchTerm, setSearchTerm] = useState(filters.search_term || "");
  const [dateRange, setDateRange] = useState({
    created_after: filters.created_after || undefined,
    created_before: filters.created_before || undefined
  });

  // Apply search when user stops typing
  useEffect(() => {
    const delayTimer = setTimeout(() => {
      if (searchTerm !== filters.search_term) {
        onFilterChange({ search_term: searchTerm || undefined });
      }
    }, 500);

    return () => clearTimeout(delayTimer);
  }, [searchTerm, filters.search_term, onFilterChange]);

  const handleChangeStatus = (status: string) => {
    // Convert "all" to undefined to clear the filter
    onFilterChange({ status: status === "all" ? undefined : status as any });
  };

  const handleChangeListingType = (type: string) => {
    // Convert "all" to undefined to clear the filter
    onFilterChange({ listing_type: type === "all" ? undefined : type as any });
  };

  const handleSortByChange = (sortBy: string) => {
    onFilterChange({ sort_by: sortBy });
  };

  const handleSortOrderChange = (order: string) => {
    onFilterChange({ sort_order: order as "asc" | "desc" });
  };

  const handleDateChange = (date: Date | undefined, type: 'after' | 'before') => {
    if (type === 'after') {
      const formattedDate = date ? format(date, "yyyy-MM-dd") : undefined;
      setDateRange(prev => ({ ...prev, created_after: formattedDate }));
      onFilterChange({ created_after: formattedDate });
    } else {
      const formattedDate = date ? format(date, "yyyy-MM-dd") : undefined;
      setDateRange(prev => ({ ...prev, created_before: formattedDate }));
      onFilterChange({ created_before: formattedDate });
    }
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setDateRange({
      created_after: undefined,
      created_before: undefined
    });
    onReset();
  };
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-medium">Filters</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search listings..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Status</Label>
          <Select 
            value={filters.status || "all"} 
            onValueChange={handleChangeStatus}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Listing Type</Label>
          <Select 
            value={filters.listing_type || "all"} 
            onValueChange={handleChangeListingType}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="sale">For Sale</SelectItem>
              <SelectItem value="rent">For Rent</SelectItem>
              <SelectItem value="lease">For Lease</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>Created After</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !dateRange.created_after && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateRange.created_after ? format(new Date(dateRange.created_after), "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={dateRange.created_after ? new Date(dateRange.created_after) : undefined}
                onSelect={(date) => handleDateChange(date, 'after')}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        
        <div className="space-y-2">
          <Label>Created Before</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !dateRange.created_before && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateRange.created_before ? format(new Date(dateRange.created_before), "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={dateRange.created_before ? new Date(dateRange.created_before) : undefined}
                onSelect={(date) => handleDateChange(date, 'before')}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>Sort By</Label>
          <Select 
            value={filters.sort_by || "created_at"} 
            onValueChange={handleSortByChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Date Created</SelectItem>
              <SelectItem value="updated_at">Date Updated</SelectItem>
              <SelectItem value="price">Price</SelectItem>
              <SelectItem value="title">Title</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>Sort Order</Label>
          <Select 
            value={filters.sort_order || "desc"} 
            onValueChange={handleSortOrderChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sort order" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Descending</SelectItem>
              <SelectItem value="asc">Ascending</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button 
          variant="outline" 
          className="w-full" 
          onClick={handleResetFilters}
        >
          Reset Filters
        </Button>
      </CardContent>
    </Card>
  );
}
